import { isFieldRoleBound } from './fieldBindings';
import {
  LAYOUT_COMPONENT_TYPE,
  type LayoutBlock,
  type LocalBusinessSocialLinks,
} from './studio';

/** Curated networks — aligns with LocalBusinessSocialLinksSchema + SITE_SOCIAL_ICONS. */
export const SITE_SOCIAL_NETWORK_KEYS = [
  'facebook',
  'instagram',
  'tiktok',
  'x',
  'youtube',
] as const;

export type SiteSocialNetworkKey = (typeof SITE_SOCIAL_NETWORK_KEYS)[number];

/**
 * Block content keys that receive hydrated social URLs (locale-identical).
 * Shared by Footer + Navigation (hamburger drawer) — same Local Presence store.
 */
export const SITE_SOCIAL_CONTENT_KEY_BY_NETWORK = {
  facebook: 'socialFacebook',
  instagram: 'socialInstagram',
  tiktok: 'socialTiktok',
  x: 'socialX',
  youtube: 'socialYoutube',
} as const satisfies Record<SiteSocialNetworkKey, string>;

export const SITE_SOCIAL_CONTENT_KEYS = [
  SITE_SOCIAL_CONTENT_KEY_BY_NETWORK.facebook,
  SITE_SOCIAL_CONTENT_KEY_BY_NETWORK.instagram,
  SITE_SOCIAL_CONTENT_KEY_BY_NETWORK.tiktok,
  SITE_SOCIAL_CONTENT_KEY_BY_NETWORK.x,
  SITE_SOCIAL_CONTENT_KEY_BY_NETWORK.youtube,
] as const;

export type SiteSocialContentKey = (typeof SITE_SOCIAL_CONTENT_KEYS)[number];

export type ActiveSocialLink = {
  network: SiteSocialNetworkKey;
  url: string;
};

/** Active profiles from Local Presence branding (trimmed non-empty HTTPS URLs). */
export function listActiveSocialLinks(
  links: LocalBusinessSocialLinks | null | undefined,
): ActiveSocialLink[] {
  if (!links) {
    return [];
  }
  const out: ActiveSocialLink[] = [];
  for (const network of SITE_SOCIAL_NETWORK_KEYS) {
    const url = links[network]?.trim();
    if (url) {
      out.push({ network, url });
    }
  }
  return out;
}

/** Map a content field key back to its social network (or null). */
export function socialNetworkFromContentKey(fieldKey: string): SiteSocialNetworkKey | null {
  for (const network of SITE_SOCIAL_NETWORK_KEYS) {
    if (SITE_SOCIAL_CONTENT_KEY_BY_NETWORK[network] === fieldKey) {
      return network;
    }
  }
  return null;
}

/**
 * Canvas / Zone 4 field keys that edit Local Presence social profiles
 * (`socialLinks` aggregate + hydrated `socialFacebook` / … keys).
 */
export function isSiteSocialFieldKey(fieldKey: string): boolean {
  return fieldKey === 'socialLinks' || socialNetworkFromContentKey(fieldKey) != null;
}

type LocalizedContent = Record<string, Record<string, string> | undefined>;

/**
 * Read hydrated social URLs from block content (same URL every locale).
 * Prefers the active locale, then any non-empty sibling locale.
 */
export function pickSiteSocialLinksFromContent(
  content: LocalizedContent | null | undefined,
  locale: string,
): ActiveSocialLink[] {
  if (!content) {
    return [];
  }
  const out: ActiveSocialLink[] = [];
  for (const network of SITE_SOCIAL_NETWORK_KEYS) {
    const fieldKey = SITE_SOCIAL_CONTENT_KEY_BY_NETWORK[network];
    const localized = content[fieldKey];
    if (!localized) {
      continue;
    }
    const preferred = localized[locale]?.trim();
    const fallback = Object.values(localized)
      .map((value) => value?.trim())
      .find((value) => Boolean(value));
    const url = preferred || fallback;
    if (url) {
      out.push({ network, url });
    }
  }
  return out;
}

/**
 * Clear + rewrite social content keys from branding (footer, nav drawer, …).
 * Mutates `content` in place.
 */
export function projectSocialLinksOntoContent(
  content: Record<string, Record<string, string>>,
  socialLinks: LocalBusinessSocialLinks | null | undefined,
  locales: string[],
): void {
  for (const key of SITE_SOCIAL_CONTENT_KEYS) {
    delete content[key];
  }
  for (const { network, url } of listActiveSocialLinks(socialLinks)) {
    const fieldKey = SITE_SOCIAL_CONTENT_KEY_BY_NETWORK[network];
    const record: Record<string, string> = {};
    for (const locale of locales) {
      record[locale] = url;
    }
    content[fieldKey] = record;
  }
}

function isChromeSocialHostBlock(block: LayoutBlock): boolean {
  return (
    block.componentType === LAYOUT_COMPONENT_TYPE.footerStandard ||
    block.componentType === LAYOUT_COMPONENT_TYPE.navigationHeader ||
    block.componentType === LAYOUT_COMPONENT_TYPE.contactChannelsFloat ||
    block.componentType === LAYOUT_COMPONENT_TYPE.utilityBar
  );
}

/**
 * Project Persona social URLs onto bound Footer / Nav / Contact Channels blocks.
 * Used after Local Presence save so the workspace store (and canvas) update even
 * when a hydrate-only bridge pass is skipped or dropped.
 */
function socialContentFingerprint(
  content: Record<string, Record<string, string> | undefined>,
): string {
  return SITE_SOCIAL_CONTENT_KEYS.map((key) => {
    const localized = content[key];
    if (!localized) return `${key}=`;
    return `${key}=${Object.keys(localized)
      .sort()
      .map((locale) => `${locale}:${localized[locale] ?? ''}`)
      .join(',')}`;
  }).join('|');
}

export function projectSocialLinksOntoBoundChromeBlocks(
  blocks: LayoutBlock[],
  socialLinks: LocalBusinessSocialLinks | null | undefined,
  locales: string[],
): LayoutBlock[] {
  if (blocks.length === 0) {
    return blocks;
  }
  let changed = false;
  const next = blocks.map((block) => {
    if (!isChromeSocialHostBlock(block)) {
      return block;
    }
    if (!isFieldRoleBound(block.fieldBindings, 'socialLinks')) {
      return block;
    }
    const content: Record<string, Record<string, string>> = { ...block.content };
    const before = socialContentFingerprint(content);
    projectSocialLinksOntoContent(content, socialLinks, locales);
    if (socialContentFingerprint(content) === before) {
      return block;
    }
    changed = true;
    return { ...block, content };
  });
  return changed ? next : blocks;
}
